import { cos, sin } from './dmath.ts';

/** Gameplay constants shared by the client and the server. Names, colours and copy stay in the client. */

export type Kind = 'r' | 'b' | 'y' | 'g' | 'a' | 'w' | 'v' | 't' | 'o' | 'k' | 's';
/** Stable ids: ember, frost, sun, venom, arcane, prism, blood, time, gravity. */
export const KINDS: Kind[] = ['r', 'b', 'y', 'g', 'a', 'w', 'v', 't', 'o', 'k', 's'];
/** Summon odds: the prism (a wild card) is rare. */
export const SUMMON_WEIGHTS: Record<Kind, number> = { r: 1, b: 1, y: 1, g: 1, a: 1, w: 0.45, v: 1, t: 1, o: 1, k: 1, s: 1 };
/** The prism merges with any relic of its level and becomes that relic's next grade. */
export const WILD: Kind = 'w';
/** Gem grades: 1 (rough) … 5 (legendary). */
export const MAX_LEVEL = 5;

export interface KindStats {
  /** Seconds between shots at level 1. */
  interval: number;
  damage: number;
  projectileSpeed: number;
  /** Anticipation is part of the firing interval, not an extra cooldown. */
  windup: number;
}
export const KIND_STATS: Record<Kind, KindStats> = {
  // ember (legendary): a modest flame alone; buffs act on it threefold (AMPLIFY)
  r: { interval: 1.35, damage: 26, projectileSpeed: 11, windup: 0.2 },
  b: { interval: 1.6, damage: 11, projectileSpeed: 14, windup: 0.38 },
  y: { interval: 1.25, damage: 14, projectileSpeed: 18, windup: 0.12 },
  g: { interval: 2.4, damage: 19, projectileSpeed: 7, windup: 0.48 },
  // arcane: rapid single-target missiles, the steadiest damage on one enemy
  a: { interval: 0.42, damage: 19, projectileSpeed: 16, windup: 0.05 },
  // prism: no attack of its own; its value is the merge
  w: { interval: 1e9, damage: 0, projectileSpeed: 0, windup: 0 },
  v: { interval: 1.5, damage: 42, projectileSpeed: 0, windup: 0.25 },
  // time: stops a few enemies and strikes them as it does
  t: { interval: 3.0, damage: 48, projectileSpeed: 0, windup: 0.42 },
  // gravity: opens a well on the road; damage is per second inside it
  o: { interval: 3.6, damage: 34, projectileSpeed: 0, windup: 0.3 },
  // obsidian: a bolt that marks its target (MARK) — everyone hits a marked enemy harder
  k: { interval: 1.8, damage: 34, projectileSpeed: 14, windup: 0.2 },
  // storm eye (legendary): a weak bolt that grows with every kill of its own, the whole match (STORM)
  s: { interval: 1.3, damage: 14, projectileSpeed: 14, windup: 0.2 },
};
/** Obsidian's mark: a marked enemy takes `power`× from everyone, for `duration + perLevel × rank` s. */
export const MARK = { power: 1.5, duration: 3.5, perLevel: 0.5 };
/** The storm eye: each kill of its own adds `perKill` to its blow, for the rest of the match (linear). */
export const STORM = { perKill: 0.01 };
/**
 * The spheres' rarity (docs/spheres.md): commons are plain — a modest blow, no great buff — and every new
 * player has them; rares are the cunning ones (control, charge); legendaries change a build: the prism's
 * wild merge, the ember's amplified buffs.
 */
export type Rarity = 'common' | 'rare' | 'legendary';
export const RARITY: Record<Kind, Rarity> = { b: 'common', y: 'common', a: 'common', g: 'common', v: 'rare', o: 'rare', t: 'rare', k: 'rare', r: 'legendary', w: 'legendary', s: 'legendary' };
/** A legendary ember: what any buff adds to a relic (totem, citrine's link) it adds threefold; overcharge one and a half. */
export const AMPLIFY: Partial<Record<Kind, { buff: number; haste: number }>> = { r: { buff: 3, haste: 1.5 } };
/**
 * Sphere mastery (the meta progression): 0…10 levels, each +1.5% to a sphere's damage, its attack pace and
 * its signature effect (the sundial's stop, onyx's well, obsidian's mark, sapphire's chill, the garnet's
 * charge) — about +32% damage a second at 10. The balance is set at 10 (a mastered deck, docs/spheres.md);
 * a fresh one ends sooner. Until progression exists every sphere plays at `calibrated`.
 */
export const MASTERY = { max: 10, step: 0.015, calibrated: 10 };
/** What mastery `level` multiplies a sphere's damage, pace and signature effect by. */
export const masteryPower = (level: number) => 1 + MASTERY.step * level;
/** A mastery level as the sim holds it: an integer 0…MASTERY.max. */
export const clampMastery = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) ? Math.min(MASTERY.max, Math.max(0, v)) : MASTERY.calibrated);
/** A deck's mastery, if sent: one integer 0…MASTERY.max per picked sphere. */
export const validMastery = (m: unknown, n: number) =>
  m === undefined || (Array.isArray(m) && m.length === n && m.every((v) => Number.isInteger(v) && v >= 0 && v <= MASTERY.max));
/** Additional charge per finishing blow, on top of the victim's ordinary soul reward. */
export const VAMPIRE_CHARGE = [0.18, 0.22, 0.26, 0.3, 0.34] as const;
export const TIME_STOP = {
  duration: [0.55, 0.65, 0.75, 0.85, 0.95],
  targets: [1, 1, 2, 3, 4],
  radius: [0, 0, 0.62, 0.8, 0.95],
} as const;
/** Onyx gravity well by rank: radius (m), lifetime (s), pull toward the centre (m/s), slow. */
export const GRAVITY = {
  radius: [0.8, 0.9, 1.0, 1.12, 1.25],
  duration: [1.8, 2.1, 2.4, 2.7, 3.0],
  pull: [0.45, 0.55, 0.65, 0.75, 0.9],
  slow: [0.35, 0.4, 0.45, 0.5, 0.6],
} as const;

/** The altars' grids (rows × cols), 4×4 in both modes. A match starts with them empty: every relic is summoned. */
const grid = (n: number) => Array.from({ length: n }, () => Array.from({ length: n }, () => ''));
export const BOARD_COOP = grid(4);
export const BOARD_SOLO = grid(4);
export const BOARD_PARTNER = grid(4);

/** Enemies are drawn and collide 5% smaller than their base proportions. */
export const ENEMY_SCALE = 0.95;

const GATE_ANGLE = -0.7853981633974483; // −π/4
/** World layout (units ≈ metres). */
export const L = {
  roadZ: -2.0,
  roadDepth: 2.5,
  get roadLength() { return this.gateX - this.spawnX + 1.6; },
  get roadCenterX() { return (this.gateX + this.spawnX) / 2; },
  gateX: 5.0,
  get gateZ() { return this.roadZ; },
  gateAngle: GATE_ANGLE,
  gateW: 1.55,
  gateWallHalf: 1.55,
  wallH: 1.55,
  /** The straight side wall continues beyond the visible courtyard. */
  wallZ: -38,
  get cornerX() { return this.gateX - cos(this.gateAngle) * this.gateWallHalf; },
  get cornerZ() { return this.gateZ + sin(this.gateAngle) * this.gateWallHalf; },
  get curbZ() { return this.roadZ + this.roadDepth / 2 + 0.16; },
  /** Lanes (z) the horde marches in. */
  lanes: [-2.86, -2.34, -1.82, -1.30],
  /** Enemies are born deep inside the fog bank… */
  spawnX: -8.6,
  /** …the visible bank's dense edge and where it thins out… */
  fogStartX: -7.2,
  fogEndX: -5.2,
  /** …and they finish materialising while the fog around them is still thick. */
  revealStartX: -8.2,
  revealEndX: -6.5,
  partnerZ: -6.25,
  sealX: 0.65,
  sealScale: 0.78,
};

export type SkillId = 'meteor' | 'barrier' | 'haste' | 'frost';
export const SKILL_IDS: SkillId[] = ['meteor', 'barrier', 'haste', 'frost'];
export const SKILL_COOLDOWN: Record<SkillId, number> = { meteor: 18, barrier: 24, haste: 22, frost: 28 };
/**
 * One active skill per player, picked in the lobby: it is sent as the match's first action and taken
 * only in its opening seconds (a pick, not a swap mid-battle). Before any pick a player holds `default`.
 */
export const SKILL_PICK = { default: 'meteor' as SkillId, window: 5 };
/**
 * The deck, built in the lobby: four spheres of the player's choosing — the prism, the wild card, is one of
 * them if picked, never a free fifth. The limit is the board's: wells / (ranks below V) = 16 / 4 = 4
 * kinds, so a full board without a merge (below V) doesn't happen by luck (docs/deck-study.md: no such
 * block in any match with four; five kinds block most). The same in every mode (solo, co-op, PvP) and
 * whatever the collection grows to: new spheres widen the choice, not the deck. Taken, like the skill,
 * in the match's opening SKILL_PICK.window seconds.
 */
export const DECK = { size: 4, attackers: 2, default: ['r', 'b', 'y', 'a'] as Kind[] };
/**
 * The elements that kill on their own (rank I, damage a second: ember ~36, blood ~25, arcane ~21, frost ~15 over an
 * area, sun ~11 chained); the rest support (venom's pools, gravity's well, the sundial's stop) and the prism
 * doesn't shoot. A deck with fewer than DECK.attackers of them falls in the first minutes: the lobby says so.
 */
export const ATTACKERS: readonly Kind[] = ['r', 'b', 'y', 'a', 'v', 'k', 's'];
/** A deck as the seal holds it: the picked spheres in KINDS order. */
export const deckOf = (kinds: readonly Kind[]): Kind[] => KINDS.filter((k) => kinds.includes(k));
/** A valid pick: exactly DECK.size distinct spheres (the prism may be one of them). */
export const validDeck = (kinds: readonly Kind[]) =>
  kinds.length === DECK.size && new Set(kinds).size === kinds.length && kinds.every((k) => KINDS.includes(k));
/** Skills aimed at a point on the road. */
export const TARGETED: Record<SkillId, boolean> = { meteor: true, barrier: true, haste: false, frost: true };
/** Frost wave: radius, expansion time and how long everyone it touches stays frozen (bosses included). */
export const FROST = { radius: 1.75, spread: 0.35, freeze: 2.6 };
export const METEOR = { count: 6, fall: 0.52, stagger: 0.2, radius: 0.9, damage: 100, burnRadius: 1.35, burnTime: 4.5, burnDps: 18 };
export const BARRIER = { hp: 380, life: 9 };
export const HASTE = { time: 6 };

export type TotemKind = 'wind' | 'might' | 'ascend' | 'shatter';
export const TOTEM_KINDS: TotemKind[] = ['wind', 'might', 'ascend', 'shatter'];
/**
 * `period`: the totem's charge in seconds — the ascend totem raises one lowest-level relic by a level when
 * it fills; the shatter totem's full charge lets the player break any one sphere (the `shatter` action) to
 * free its well, and waits until used.
 */
export const TOTEM_STATS: Record<TotemKind, { rate: number; power: number; period: number }> = {
  wind: { rate: 1.15, power: 1, period: 0 },
  might: { rate: 1, power: 1.2, period: 0 },
  ascend: { rate: 1, power: 1, period: 45 },
  shatter: { rate: 1, power: 1, period: 60 },
};

export const RUNNER = { speed: 1.28, hp: 38, firstSpawn: 45, minInterval: 8, maxInterval: 13, maxAlive: 4 };
export const PITBOSS = { speed: 0.27, hp: 1500, scale: 1.14, mass: 5, radius: 0.44, reward: 10, firstSpawn: 180, minInterval: 32, maxInterval: 44, maxAlive: 2 };

/** Seal energy: the cost of the next summon grows with every summon. */
/** The gate holds this many breaches; the one that reaches it ends the match («Брама впала»). */
export const GATE = { breaches: 10 };
/**
 * The horde's build-up: the first soldier steps out of the fog after `firstSoldier` s, alone; gaps start
 * `calm`× the steady ones and close to them by `rampEnd` s. Runners, then the first boss, then the pit boss
 * join on their own timers (RUNNER.firstSpawn, `firstBoss`, PITBOSS.firstSpawn).
 */
export const RAMP = { firstSoldier: 3, calm: 6, rampEnd: 150, firstBoss: 90 };
/**
 * Surges: every 45–90 s a pack walks out of the fog at once — `base` soldiers and one more every `grow` s
 * of the match, now and then (`runners`) two runners with them. Pressure comes in waves, not only a rising
 * line: an early surge can break a weak start, a late one a good board (variance at every stage).
 */
export const SURGE = { first: [45, 75] as const, every: [45, 90] as const, base: 6, grow: 45, spread: 3, runners: 0.45 };
export const SEAL = { start: 6, trickle: 0.3, baseCost: 3, maxCost: 24 };
/**
 * The balance (docs/balance.md), calibrated by simulation against a merging-first player:
 * - RANK_DAMAGE: a rank about doubles a relic, so a merge (two into one) is a gain, not a loss.
 * - HORDE_HP: the horde's health, ×(1 + t/linear + (t/quad)²) — the first minutes as before, the late
 *   game steeper, so a well-built board falls at ~15 min solo, ~25 min co-op (two boards: a longer quad).
 * - SEAL.maxCost 24: the price climbs on, so development spans the match (first V ~6 min solo).
 * - The ascend totem every 45 s: a full board of V stays a rare feat.
 */
export const RANK_DAMAGE = [1, 1.9, 3.6, 6.9, 13] as const;
export const HORDE_HP = { linear: { soldier: 90, elite: 120 }, quad: { solo: 160, coop: 187 } };
/** The horde's health multiplier at `clock` seconds, for soldiers or elites (bosses, runners). */
export const hordeHp = (clock: number, kind: 'soldier' | 'elite', mode: 'solo' | 'coop') => {
  const q = clock / HORDE_HP.quad[mode];
  return 1 + clock / HORDE_HP.linear[kind] + q * q;
};
/** How long a summoned relic flies from the seal to its well, a merge's charge and the ascend rite. */
export const TIMING = { summonFlight: 0.42, ascendRite: 0.5 };
export const mergeCharge = (newLevel: number) => 0.24 + 0.09 * (newLevel - 2);
